import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  TextField,
  Stack,
  Grid,
  Autocomplete,
  Alert,
  Typography,
} from "@common-ui/ui";
import InputLabel from "@mui/material/InputLabel";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  getOrgServiceCategories,
  createService,
} from "../../services/services";
import AlertDialog from "./AlertDialog";
import MandatoryIcon from "../../components/MandatoryIcon";

export function CreateServiceDialog(props) {
  // eslint-disable-next-line no-unused-vars
  const currentOrgId = useSelector(
    (state) => state.organization?.currentOrganization?.id
  );

  const [open, setOpen] = React.useState(false);
  const [planSystemId, setPlanSystemId] = useState(null);
  const [, setEntryMode] = useState("");
  const [categories, setCategories] = useState([]);
  let router = useHistory();
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchData() {
    try {
      let categories = await getOrgServiceCategories(currentOrgId);
      setCategories(categories);
    } catch (error) {
      alert(error);
    }
  }

  const formik = useFormik({
    initialValues: {
      name: "",
      category: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().max(255).required("name is required"),
      category: Yup.object().nullable().required("Category is required"),
    }),
    onSubmit: () => {
      async function submit() {
        let { name, category } = formik.values;
        let attributes = {
          name,
          category: category.name,
        };
        try {
          const payload = await onSubmit(attributes, currentOrgId);
          setPlanSystemId(payload?.system_id);
          setOpen(true);
        } catch (error) {
          alert(error.message);
          formik.setSubmitting(false);
        }
      }
      submit();
    },
  });

  const onSubmit = async (attributes, orgId) => {
    // console.log("attributes", attributes);
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const payload = await createService(attributes, orgId);
        resolve(payload);
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleClose = () => {
    router.push({
      pathname: `/services/${currentOrgId}/${planSystemId}/edit`,
    });
    setOpen(false);
  };

  const descriptionElementRef = React.useRef(null);
  React.useEffect(() => {
    if (props.open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [props.open]);

  return (
    <div>
      <Dialog
        open={props.open}
        onClose={() => {
          formik.handleReset();
          props.handleClose();
          setEntryMode("");
        }}
        aria-labelledby="alert-dialog-name"
        aria-describedby="alert-dialog-description"
        scroll="paper"
        fullWidth
      >
        <>
          <DialogTitle id="alert-dialog-title" scroll="paper">
            <Stack sx={{ alignItems: "center" }}>
              <Typography variant="h4">Create a New Service</Typography>
            </Stack>
            {props.open ? (
              <IconButton
                aria-label="close"
                onClick={() => {
                  props.handleClose();
                  formik.handleReset();
                  setEntryMode("");
                }}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </DialogTitle>

          <DialogContent dividers={true}>
            <Box
              component="main"
              sx={{
                alignItems: "center",
                display: "flex",
                flexGrow: 1,
                minHeight: "100%",
                m: 0,
                p: 0,
              }}
            >
              <Grid
                container
                spacing={2}
                style={{
                  paddingLeft: "0 !important",
                  paddingRight: "0 !important",
                }}
              >
                {!currentOrgId ? (
                  <Grid item md={12} xs={12}>
                    <Alert
                      color="warning"
                      severity="warning"
                      variant="standard"
                    >
                      You are not a member of an organization. You need to be a
                      member of at least an organization to create a maintenance
                      plan.
                    </Alert>
                  </Grid>
                ) : (
                  ""
                )}
                <Grid item md={6}>
                  <InputLabel>
                    {" "}
                    <MandatoryIcon />
                    Service Category
                  </InputLabel>
                  <Autocomplete
                    id="country-select"
                    required
                    // disablePortal
                    // disabled={!customers?.length}
                    ListboxProps={{ style: { maxHeight: "200px" } }}
                    options={categories}
                    autoHighlight
                    size="small"
                    getOptionLabel={(option) => option?.name}
                    value={formik.values.category}
                    onChange={(value, newValue) => {
                      formik.setFieldValue("category", newValue);
                    }}
                    margin="normal"
                    name="category"
                    limitTags={4}
                    fullWidth
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                        {...props}
                      >
                        {option?.name}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select a category"
                        margin="normal"
                        sx={{ mt: 1 }}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "off", // disable autocomplete and autofill
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <div>
                    <InputLabel>
                      <MandatoryIcon />
                      Service Name
                    </InputLabel>
                    <TextField
                      sx={{ mt: 1 }}
                      size="small"
                      autoComplete="off"
                      error={Boolean(formik.touched.name && formik.errors.name)}
                      fullWidth
                      helperText={formik.touched.name && formik.errors.name}
                      placeholder="Enter service name"
                      margin="normal"
                      name="name"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.name}
                      variant="outlined"
                    />
                  </div>
                </Grid>
              </Grid>
            </Box>
            <AlertDialog
              handleClose={handleClose}
              open={open}
              name={formik.values.name}
            />
          </DialogContent>
          <DialogActions>
            <div>
              <Button
                color="primary"
                fullWidth
                size="large"
                onClick={() => {
                  props.handleClose();
                  formik.handleReset();
                  setEntryMode("");
                }}
                variant="text"
              >
                Cancel
              </Button>
            </div>
            <div>
              <Button
                color="primary"
                disabled={
                  !(formik.isValid && formik.dirty && !formik.isSubmitting)
                }
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                onClick={formik.handleSubmit}
              >
                Save
              </Button>
            </div>
          </DialogActions>
        </>
      </Dialog>
    </div>
  );
}
